import { useEffect, useState } from 'react';

import { format, getISOWeek } from 'date-fns';

import UserMenu from '@/features/auth/UserMenu';
import ThemeSwitcher from '@/features/theme/ThemeSwitcher';

import HeaderSearch from './components/HeaderSearch';
import { useDashboardBreakpoint } from './hooks/useDashboardBreakpoint';

/**
 * Top-of-dashboard chrome: a minute-precision clock, the long date + ISO week,
 * the header search (wide only), the theme pill, and the account avatar. The
 * clock is part of the layout — it is no longer a grid widget.
 */
export default function DashboardHeader() {
  const now = useMinuteClock();
  const isWide = useDashboardBreakpoint() === 'wide';

  return (
    <header className="tablet:mb-5 mb-4 flex items-center gap-3 px-1 sm:gap-4">
      <span className="font-display tablet:text-4xl text-3xl leading-none font-semibold tracking-tight text-(--text-primary) tabular-nums">
        {format(now, 'HH:mm')}
      </span>
      <span className="tablet:hidden truncate text-xs text-(--text-tertiary)">
        {format(now, 'EEE d LLL')} · W{getISOWeek(now)}
      </span>
      <span className="tablet:inline hidden text-base text-(--text-tertiary)">
        {format(now, 'EEEE d LLLL')} · Week {getISOWeek(now)}
      </span>
      <span className="flex-1" />
      {isWide && (
        <>
          <HeaderSearch />
          <span className="flex-1" />
        </>
      )}
      <ThemeSwitcher />
      <UserMenu />
    </header>
  );
}

/** Ticks exactly on each minute boundary to avoid per-second re-renders. */
function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timer: number;
    const schedule = () => {
      const msToNextMinute = 60_000 - (Date.now() % 60_000);
      timer = window.setTimeout(() => {
        setNow(new Date());
        schedule();
      }, msToNextMinute + 50);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, []);
  return now;
}
